import chroma from 'chroma-js';

export interface CrmColors {
    pageBg: string;
    columnBg: string;
    cardBg: string;
    cardBgDragging: string;
    surfaceBg: string;
    menuBg: string;
    /// Fundo da thread multicanal, mais escuro que o balão de propósito: sem a diferença o balão some.
    threadBg: string;
    bubbleIncoming: string;
    bubbleOutgoing: string;
    textPrimary: string;
    textSecondary: string;
    textMuted: string;
    /// Texto de botão e etiqueta com fundo colorido: na mesma cor da base, o texto apagaria.
    onTint: string;
    border: string;
    borderStrong: string;
    borderHover: string;
    divider: string;
    dividerStrong: string;
    countBg: string;
    accentSoftBg: string;
    scheduleSoon: string;
    scheduleLate: string;
    accent: string;
    eventNeutral: string;
    eventSuccess: string;
    eventWarning: string;
    eventDanger: string;
}

/// Mesmos tons do tema escuro: superfície na cor do menu lateral e o que fica em cima um degrau acima.
const DARK: CrmColors = {
    pageBg: 'transparent',
    columnBg: '#141c2b',
    cardBg: '#1b2536',
    cardBgDragging: '#233049',
    surfaceBg: '#141c2b',
    menuBg: '#1b2536',
    threadBg: '#0f1623',
    bubbleIncoming: '#1f2a3e',
    bubbleOutgoing: '#14417d',
    textPrimary: '#e9eef5',
    textSecondary: '#9aadc2',
    textMuted: '#7c93a9',
    onTint: '#ffffff',
    border: 'rgba(255,255,255,.08)',
    borderStrong: 'rgba(255,255,255,.12)',
    borderHover: 'rgba(255,255,255,.28)',
    divider: 'rgba(255,255,255,.06)',
    dividerStrong: 'rgba(255,255,255,.14)',
    countBg: 'rgba(255,255,255,.07)',
    accentSoftBg: 'rgba(52,87,224,.16)',
    scheduleSoon: '#38bdf8',
    scheduleLate: '#f97066',
    accent: '#1A73E8',
    eventNeutral: '#94a3b8',
    eventSuccess: '#12b76a',
    eventWarning: '#f79009',
    eventDanger: '#f97066',
};

const LIGHT: CrmColors = {
    pageBg: 'transparent',
    columnBg: '#f4f7fb',
    cardBg: '#ffffff',
    cardBgDragging: '#e7eefb',
    surfaceBg: '#ffffff',
    menuBg: '#ffffff',
    threadBg: '#eef2f7',
    bubbleIncoming: '#ffffff',
    bubbleOutgoing: '#d7e6fb',
    textPrimary: '#1a2530',
    textSecondary: '#52657a',
    textMuted: '#5f7285',
    onTint: '#1a2530',
    border: 'rgba(0,0,0,.10)',
    borderStrong: 'rgba(0,0,0,.16)',
    borderHover: 'rgba(0,0,0,.30)',
    divider: 'rgba(0,0,0,.08)',
    dividerStrong: 'rgba(0,0,0,.14)',
    countBg: 'rgba(0,0,0,.05)',
    accentSoftBg: 'rgba(52,87,224,.10)',
    scheduleSoon: '#0369a1',
    scheduleLate: '#d92d20',
    accent: '#1662C4',
    eventNeutral: '#64748b',
    eventSuccess: '#027a48',
    eventWarning: '#b54708',
    eventDanger: '#d92d20',
};

export const getCrmColors = (darkMode: boolean): CrmColors => (darkMode ? DARK : LIGHT);

/// O usuário escolhe a cor do status, então a tinta do texto sobre ela não pode ser fixa.
export const inkOver = (background: string): string => {
    try {
        return chroma.contrast(background, '#10202f') >= chroma.contrast(background, '#ffffff')
            ? '#10202f'
            : '#ffffff';
    } catch {
        return '#ffffff';
    }
};

export type SidenavColor =
    | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' | 'light' | 'dark';

const BOARD_COLORS = [
    '#e91e63',
    '#3457e0',
    '#12b76a',
    '#f79009',
    '#7c4dff',
    '#00bcd4',
    '#ef4444',
    '#0ea5e9',
];

export const getBoardColor = (boardId: string): string => {
    if (!boardId) {
        return BOARD_COLORS[0];
    }

    const sum = [...boardId].reduce((total, char) => total + char.charCodeAt(0), 0);

    return BOARD_COLORS[sum % BOARD_COLORS.length];
};
